import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { nanoid } from "nanoid";
import {
  getTaxConfigs, saveTaxConfigs,
  getTaxAssignments, saveTaxAssignments
} from "../utils/taxesApi";

export function useTaxAssignments(businessId, products) {
  // Server-sourced configs
  const [configs, setConfigs] = useState([]);
  // Editable "draft" configs (includes unsaved rows)
  const [draftConfigs, setDraftConfigs] = useState([]);

  // assignments: { [taxIdOrClientId]: productId[] }
  const [assignments, setAssignments] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // cache of last server state (for hasChanges)
  const serverSnapshot = useRef({ configs: [], assignments: {} });

  const reload = useCallback(async () => {
    setError(null);
    try {
      const [c, a] = await Promise.all([
        getTaxConfigs(businessId),
        getTaxAssignments(businessId),
      ]);
      const list = (c.taxConfigs || []).map(x => ({
        ...x,
        __cidOrId: String(x.id),      // stable key for both server & client
        __new: false,
      }));
      setConfigs(list);
      setDraftConfigs(list);
      setAssignments(a.assignments || {});
      serverSnapshot.current = {
        configs: list,
        assignments: a.assignments || {},
      };
    } catch {
      setConfigs([]);
      setDraftConfigs([]);
      setAssignments({});
      setError("Could not load tax data");
    }
  }, [businessId]);

  useEffect(() => { reload(); }, [reload]);

  // ---- Draft config CRUD ----------------------------------------------------
  const addDraftConfig = useCallback(() => {
    setDraftConfigs(d => [
      {
        __new: true,
        __cidOrId: `new_${nanoid()}`,
        name: "",
        rate_percent: "",
        applies_to_all: false,
      },
      ...d,
    ]);
  }, []);

  const updateDraftConfig = useCallback((cid, patch) => {
    setDraftConfigs(d => d.map(c => (c.__cidOrId === cid ? { ...c, ...patch } : c)));
  }, []);

  const deleteDraftConfig = useCallback((cid) => {
    setDraftConfigs(d => d.filter(c => c.__cidOrId !== cid));
    // drop any assignments pointing to that config id
    setAssignments(prev => {
      const next = { ...prev };
      delete next[cid];
      return next;
    });
  }, []);

  // ---- Assignment helpers (single-choice per product across all taxes) ------
  const assignProduct = useCallback((taxId, productId, on) => {
    setAssignments(prev => {
      // remove productId from all taxes first (keeps uniqueness and avoids conflicts)
      const cleared = Object.fromEntries(Object.entries(prev).map(([k, list]) => [k, (list||[]).filter(id => String(id) !== String(productId))]));
      if (on) {
        const cur = new Set(cleared[taxId] || []);
        cur.add(String(productId));
        cleared[taxId] = Array.from(cur);
      }
      return cleared;
    });
  }, []);

  const setAllForTax = useCallback((taxId, on) => {
    const productIds = (products || []).map(p => String(p.id));
    setAssignments(prev => {
      // clear all
      const cleared = Object.fromEntries(Object.entries(prev).map(([k, list]) => [k, (list||[]).filter(id => !productIds.includes(String(id)))]));
      if (on) cleared[taxId] = productIds;
      return cleared;
    });
  }, [products]);

  const setForCategory = useCallback((taxId, ids = [], on) => {
    const list = (ids || []).map(String);
    setAssignments(prev => {
      // clear those from all taxes
      const cleared = Object.fromEntries(Object.entries(prev).map(([k, l]) => [k, (l||[]).filter(id => !list.includes(String(id)))]));
      if (on) {
        cleared[taxId] = Array.from(new Set([...(cleared[taxId] || []), ...list]));
      }
      return cleared;
    });
  }, []);

  // ---- Derived state --------------------------------------------------------
  const productsIds = useMemo(() => (products || []).map(p => String(p.id)), [products]);

  // prune assignments to current product list
  useEffect(() => {
    setAssignments(prev => {
      const out = {};
      for (const [k, list] of Object.entries(prev || {})) {
        out[k] = (list || []).filter(id => productsIds.includes(String(id)));
      }
      return out;
    });
  }, [productsIds]);

  // Any config marked apply_to_all?
  const configsApplyAllActive = useMemo(() => (draftConfigs || []).some(c => !!c.applies_to_all), [draftConfigs]);

  // Validation
  const validation = useMemo(() => {
    const msgs = [];
    const details = {
      missingName: [],
      missingRate: [],
      invalidRate: [],
      multipleApplyAll: false,
      applyAllWithExtra: false,
      multipleTax: [],
      emptyConfigs: [],
    };

    const list = draftConfigs || [];

    // name / rate presence + correctness (rate >= 0, 0 allowed)
    for (const c of list) {
      const nm = String(c.name || "").trim();
      if (!nm) details.missingName.push(c.__cidOrId);

      const raw = c.rate_percent;
      const isBlank = raw === "" || raw == null;
      if (isBlank) {
        details.missingRate.push(c.__cidOrId);
      } else {
        const num = typeof raw === "number" ? raw : Number(raw);
        if (!Number.isFinite(num) || num < 0) {
          details.invalidRate.push(c.__cidOrId);
        }
      }
    }

    if (details.missingName.length) msgs.push("Each tax must have a name.");
    if (details.missingRate.length) msgs.push("Each tax must have a rate.");
    if (details.invalidRate.length) msgs.push("Rate must be a number ≥ 0.");

    // apply_to_all constraints
    const applyAllCount = list.filter(c => !!c.applies_to_all).length;
    if (applyAllCount > 1) {
      details.multipleApplyAll = true;
      msgs.push("Only one configuration can be set to apply to all products.");
    }
    if (applyAllCount === 1 && list.length > 1) {
      details.applyAllWithExtra = true;
      msgs.push("When a configuration applies to all products, no other tax configurations are allowed.");
    }

    // unique assignment per product (only when not apply-to-all)
    if (applyAllCount === 0) {
      const seen = new Map();
      for (const [, listP] of Object.entries(assignments || {})) {
        for (const pid of (listP || [])) {
          if (seen.has(pid)) details.multipleTax.push(pid);
          else seen.set(pid, true);
        }
      }
      if (details.multipleTax.length) {
        msgs.push("Each product can be assigned to only one tax configuration.");
      }

    // every tax config must have at least one product assigned
    const empties = (draftConfigs || []).filter(
      (c) => !(assignments?.[c.__cidOrId] || []).length
    );
    if (empties.length) {
      details.emptyConfigs = empties.map((c) => c.__cidOrId);
      const names = empties
        .map((c) => (String(c.name || "").trim() || "(unnamed)"))
        .join(", ");
      msgs.push(
        `Assign at least one product to: ${names}, or delete the configuration.`
      );
    }
        }
    return { ok: msgs.length === 0, messages: msgs, details };
  }, [draftConfigs, assignments]);

  // Dirty state
  function normalizeConfigs(list = []) {
    return list.map(c => ({
      name: String(c.name || "").trim(),
      // keep raw -> number (blank already guarded in validation; here default to 0 for snapshot)
      rate_percent: Number(c.rate_percent ?? 0),
      applies_to_all: !!c.applies_to_all,
    }));
  }
  const hasConfigChanges = useMemo(() => {
    const a = JSON.stringify(normalizeConfigs(serverSnapshot.current.configs));
    const b = JSON.stringify(normalizeConfigs(draftConfigs));
    return a !== b;
  }, [draftConfigs]);

  const hasAssignmentChanges = useMemo(() => {
    return JSON.stringify(serverSnapshot.current.assignments) !== JSON.stringify(assignments);
  }, [assignments]);

  const hasChanges = hasConfigChanges || hasAssignmentChanges;

  // ---- SAVE: Config changes first, then assignments ------------------------
  const saveConfigChanges = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      // compute diff
      const cur = draftConfigs || [];

      const added = cur
        .filter(c => c.__new)
        .map(c => ({
          client_id: c.__cidOrId,
          name: String(c.name || "").trim(),
          rate_percent: Number(c.rate_percent || 0),
          applies_to_all: !!c.applies_to_all,
        }));

      const edited = cur
        .filter(c => !c.__new)
        .map(c => ({
          id: c.__cidOrId,
          name: String(c.name || "").trim(),
          rate_percent: Number(c.rate_percent || 0),
          applies_to_all: !!c.applies_to_all,
        }));

      const removed = (serverSnapshot.current.configs || [])
        .filter(c => !cur.find(x => x.__cidOrId === c.__cidOrId))
        .map(c => c.__cidOrId);

      const payload = { added, edited, removed };

      const res = await saveTaxConfigs(businessId, payload);
      // Expect { ok, idMap?, taxConfigs? }
      const idMap = new Map(Object.entries(res?.idMap || {}));

      // reload fresh, and refresh snapshots
      await reload();

      return { ok: true, idMap };
    } catch {
      setError("Could not save tax configurations");
      return { ok: false, idMap: new Map() };
    } finally {
      setBusy(false);
    }
  }, [businessId, draftConfigs, reload]);

  const saveAssignmentsIncludingDrafts = useCallback(async ({ productIdMap = new Map(), taxIdMap = new Map() }) => {
    setBusy(true);
    setError(null);
    try {
      // Map tax ids: convert client temp ids → server ids using taxIdMap (ignore those not mapped)
      const out = {};
      for (const [tid, list] of Object.entries(assignments || {})) {
        const sid = taxIdMap.get(String(tid)) || (isServerIdLike(tid) ? String(tid) : null);
        if (!sid) continue; // skip assignments pointing to unsaved configs
        out[sid] = (list || []).map(pid => productIdMap.get(String(pid)) || String(pid));
      }
    const applyAllDraft = (draftConfigs || []).find(c => !!c.applies_to_all);
    const appliesAll =
      applyAllDraft
        ? (taxIdMap.get(String(applyAllDraft.__cidOrId)) ||
           (isServerIdLike(applyAllDraft.__cidOrId) ? String(applyAllDraft.__cidOrId) : null))
        : null;
      await saveTaxAssignments(businessId, { appliesAll, assignments: out });
      // update snapshot & reload
      serverSnapshot.current = { ...serverSnapshot.current, assignments: out };
      await reload();
      return true;
    } catch {
      setError("Could not save tax assignments");
      return false;
    } finally {
      setBusy(false);
    }
  }, [businessId, assignments, reload]);

  function isServerIdLike(x) {
    return /^\d+$/.test(String(x)) || /^[0-9a-f-]{8,}$/i.test(String(x));
  }

  return {
    // state
    configs, draftConfigs, setDraftConfigs,
    assignments,
    busy, error, setError,
    // config CRUD
    addDraftConfig, updateDraftConfig, deleteDraftConfig,
    // assign
    assignProduct, setForCategory, setAllForTax,
    // derived
    validation, hasChanges, configsApplyAllActive,
    // save
    saveConfigChanges, saveAssignmentsIncludingDrafts,
    // reload
    reload,
  };
}
